/**
 * Google OAuth Service
 * 
 * Handles Google Sign-In authentication for QuickLinks application.
 * Integrates with Google OAuth 2.0 API and backend authentication.
 * 
 * @requires google-auth-library or gapi
 */

import { makeRequest } from './api.js';

// Google OAuth configuration
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Initialize Google Sign-In
 * Loads the Google Sign-In JavaScript library
 */
export const initializeGoogleAuth = () => {
  return new Promise((resolve, reject) => {
    // Check if gapi is already loaded
    if (window.gapi) {
      resolve(window.gapi);
      return;
    }

    // Load Google API script
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('auth2', () => {
        window.gapi.auth2.init({
          client_id: GOOGLE_CLIENT_ID
        }).then(() => {
          resolve(window.gapi);
        }).catch(reject);
      });
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Sign in with Google
 * Triggers Google OAuth flow and handles authentication
 */
export const signInWithGoogle = async () => {
  try {
    // Ensure Google Auth is initialized
    await initializeGoogleAuth();
    
    const authInstance = window.gapi.auth2.getAuthInstance();
    const googleUser = await authInstance.signIn();
    
    // Get user profile information
    const profile = googleUser.getBasicProfile();
    const idToken = googleUser.getAuthResponse().id_token;
    
    const userData = {
      email: profile.getEmail(),
      name: profile.getName(),
      picture: profile.getImageUrl(),
      googleId: profile.getId(),
      idToken: idToken
    };
    
    // Send to backend for verification and user creation/login
    const response = await makeRequest('/auth/google', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    
    // Store the authentication token
    if (response.token) {
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
    }
    
    return response;
  } catch (error) {
    console.error('Google Sign-In error:', error);
    throw error;
  }
};

/**
 * Sign out from Google
 */
export const signOutFromGoogle = async () => {
  try {
    if (window.gapi && window.gapi.auth2) {
      const authInstance = window.gapi.auth2.getAuthInstance();
      await authInstance.signOut();
    }
    
    // Clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  } catch (error) {
    console.error('Google Sign-Out error:', error);
    throw error;
  }
};

/**
 * Alternative implementation using Google Identity Services (newer approach)
 * This is the recommended approach for new applications
 */

/**
 * Initialize Google Identity Services
 */
export const initializeGoogleIdentity = () => {
  return new Promise((resolve, reject) => {
    // Check if google is already loaded
    if (window.google) {
      resolve(window.google);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      resolve(window.google);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Handle Google credential response (newer API)
 */
export const handleGoogleCredentialResponse = async (response) => {
  try {
    // The response.credential contains the JWT token
    const credential = response.credential;
    
    // Send to backend for verification
    const backendResponse = await makeRequest('/auth/google-identity', {
      method: 'POST',
      body: JSON.stringify({ credential })
    });
    
    // Store the authentication token
    if (backendResponse.token) {
      localStorage.setItem('authToken', backendResponse.token);
      localStorage.setItem('user', JSON.stringify(backendResponse.user));
    }
    
    return backendResponse;
  } catch (error) {
    console.error('Google Identity credential error:', error);
    throw error;
  }
};

/**
 * Render Google Sign-In button (newer API)
 */
export const renderGoogleSignInButton = (elementId, callback) => {
  if (!GOOGLE_CLIENT_ID) {
    console.error('Google Client ID not configured');
    return;
  }

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: callback
  });

  window.google.accounts.id.renderButton(
    document.getElementById(elementId),
    {
      theme: 'outline',
      size: 'large',
      width: '100%',
      logo_alignment: 'left'
    }
  );
};

/**
 * Check if Google OAuth is properly configured
 */
export const isGoogleAuthConfigured = () => {
  return !!GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'your-google-client-id.apps.googleusercontent.com';
};
